import React from 'react';
import {
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip
} from '@mui/material';
import {
  V4_BUTTON_DEFAULT,
  V4_DIALOG_CONTENT,
  V4_ICON_BUTTON_SMALL,
  V4_OUTLINED_DEFAULT,
  V4_TABLE
} from '../../theme/v4Compat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { ClearFilters, TablePaginationWrapper, Title, ReloadIconButton, LegacyGrid as Grid } from '../../elements';
import { Skeleton } from '../../elements/ui/skeleton';
import NoDataMessage from '../shared/NoDataMessage';
import LocationPickerDialog from '../LocationPickerDialog';
import { ApiService } from '../../hooks';
import useAuthContext from '../../hooks/useAuthContext';
import { extractApiErrorMessage, notifications } from '../../utils';
import { HealthSystemAPIControllersFeedSupplyResponsesFeedSiloResponse } from '../../api/HealthSystemApi';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from '../../constants/constants';

const SilosTab: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiService = new ApiService();
  const { state } = useAuthContext();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [allSilos, setAllSilos] = React.useState<HealthSystemAPIControllersFeedSupplyResponsesFeedSiloResponse[]>([]);

  // Filters
  const [nameFilter, setNameFilter] = React.useState('');
  const [numberFilter, setNumberFilter] = React.useState('');
  const [activeFilter, setActiveFilter] = React.useState<'all' | 'active' | 'inactive'>('all');

  // Applied filters (committed on search click)
  const [appliedName, setAppliedName] = React.useState('');
  const [appliedNumber, setAppliedNumber] = React.useState('');
  const [appliedActive, setAppliedActive] = React.useState<'all' | 'active' | 'inactive'>('all');

  // Pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);

  // Create dialog
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createSiloNumber, setCreateSiloNumber] = React.useState('');
  const [createName, setCreateName] = React.useState('');
  const [createLocationIds, setCreateLocationIds] = React.useState<string[]>([]);
  const [createPickerOpen, setCreatePickerOpen] = React.useState(false);
  const [isCreating, setIsCreating] = React.useState(false);

  const loadSilos = () => {
    if (!state.farmId) return;
    setIsBusy(true);

    apiService.api.feedSilos
      .feedSilosGetByFarm({ farmId: state.farmId })
      .then((result) => {
        setAllSilos(result.data);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      loadSilos();
    }
  }, [loaded]);

  const filteredSilos = React.useMemo(() => {
    return allSilos.filter((silo) => {
      if (appliedName && !(silo.name ?? '').toLowerCase().includes(appliedName.toLowerCase())) return false;
      if (appliedNumber && !String(silo.siloNumber).includes(appliedNumber)) return false;
      if (appliedActive === 'active' && !silo.isActive) return false;
      if (appliedActive === 'inactive' && silo.isActive) return false;
      return true;
    });
  }, [allSilos, appliedName, appliedNumber, appliedActive]);

  const paginatedSilos = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredSilos.slice(start, start + rowsPerPage);
  }, [filteredSilos, page, rowsPerPage]);

  const handleSearch = () => {
    setAppliedName(nameFilter);
    setAppliedNumber(numberFilter);
    setAppliedActive(activeFilter);
    setPage(0);
  };

  const handleOpenCreate = () => {
    setCreateSiloNumber('');
    setCreateName('');
    setCreateLocationIds([]);
    setCreateOpen(true);
  };

  const handleCreateSave = () => {
    const num = parseInt(createSiloNumber, 10);
    if (!createSiloNumber || isNaN(num) || num <= 0) {
      notifications.error(t('f-val-silo-number'));
      return;
    }
    if (!state.farmId) {
      notifications.error(t('f-com-api-error'));
      return;
    }

    setIsCreating(true);
    apiService.api.feedSilos
      .feedSilosCreate(
        { farmId: state.farmId },
        {
          siloNumber: num,
          name: createName || null,
          locationIds: createLocationIds
        }
      )
      .then(() => {
        notifications.success(t('f-silo-created'));
        setCreateOpen(false);
        setLoaded(false);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  const handleClearFilters = () => {
    setNameFilter('');
    setNumberFilter('');
    setActiveFilter('all');
    setAppliedName('');
    setAppliedNumber('');
    setAppliedActive('all');
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

  const SKELETON_COLS = 6;

  return (
    <Container maxWidth={false} disableGutters sx={{ maxWidth: 1280 }}>
      <Grid container spacing={2}>
        {/* Header */}
        <Grid item container direction="row" alignItems="center">
          <Grid container item spacing={1} xs={10} alignItems="center">
            <Grid item>
              <Icon color="primary" style={{ fontSize: 28 }}>
                silo
              </Icon>
            </Grid>
            <Grid item>
              <Title>{t('f-silo-tab-silos')}</Title>
            </Grid>
            <Grid item>
              <Chip label={filteredSilos.length} variant="outlined" />
            </Grid>
          </Grid>
          <Grid item xs={2} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              {t('f-silo-create')}
            </Button>
            <ReloadIconButton action={() => setLoaded(false)} />
          </Grid>
        </Grid>

        {/* Filters */}
        <Grid item container direction="row" spacing={2}>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <TextField
              label={t('f-silo-name')}
              variant="outlined"
              size="small"
              fullWidth
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>

          <Grid item lg={3} md={4} sm={6} xs={12}>
            <TextField
              label={t('f-silo-number')}
              variant="outlined"
              size="small"
              fullWidth
              value={numberFilter}
              onChange={(e) => setNumberFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>

          <Grid item lg={3} md={4} sm={6} xs={12}>
            <Select
              variant="outlined"
              fullWidth
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as 'all' | 'active' | 'inactive')}
              size="small"
              style={{ height: 40 }}
            >
              <MenuItem value="all">{t('f-com-all')}</MenuItem>
              <MenuItem value="active">{t('f-com-active')}</MenuItem>
              <MenuItem value="inactive">{t('f-com-inactive')}</MenuItem>
            </Select>
          </Grid>

          <Grid item lg={3} md={4} sm={6} xs={12}>
            <div className="flex items-center">
              <ClearFilters onClick={handleClearFilters} />
              <Button
                variant="contained"
                color="inherit"
                sx={V4_BUTTON_DEFAULT}
                fullWidth
                onClick={handleSearch}
                startIcon={<SearchIcon />}
              >
                {t('f-search')}
              </Button>
            </div>
          </Grid>
        </Grid>

        {/* Table */}
        <Grid item xs={12}>
          <TablePaginationWrapper
            rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
            count={filteredSilos.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          >
            <TableContainer component={Paper} elevation={1}>
              <Table size="small" sx={V4_TABLE}>
                <TableHead style={{ background: '#eceff1' }}>
                  <TableRow>
                    <TableCell>{t('f-silo-number')}</TableCell>
                    <TableCell>{t('f-silo-name')}</TableCell>
                    <TableCell>{t('f-com-status')}</TableCell>
                    <TableCell>{t('f-silo-linked-locations')}</TableCell>
                    <TableCell>{t('f-com-created-at')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isBusy &&
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: SKELETON_COLS }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}

                  {!isBusy &&
                    loaded &&
                    paginatedSilos.map((silo) => (
                      <TableRow key={silo.id} hover>
                        <TableCell>{silo.siloNumber}</TableCell>
                        <TableCell>{silo.name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={silo.isActive ? t('f-com-active') : t('f-com-inactive')}
                            className={silo.isActive ? '!bg-green-100 !text-green-800' : '!bg-gray-100 !text-gray-600'}
                          />
                        </TableCell>
                        <TableCell>{silo.locations?.length ?? 0}</TableCell>
                        <TableCell>{formatDate(silo.createdAt)}</TableCell>
                        <TableCell>
                          <Tooltip arrow title={t('f-silo-view')}>
                            <IconButton
                              color="primary"
                              size="small"
                              sx={V4_ICON_BUTTON_SMALL}
                              onClick={() => navigate(`/locations/silos/${silo.id}`)}
                            >
                              <Icon>visibility</Icon>
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}

                  {!isBusy && loaded && paginatedSilos.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={SKELETON_COLS}>
                        <NoDataMessage text={t('f-no-data')} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TablePaginationWrapper>
        </Grid>
      </Grid>
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('f-silo-create')}</DialogTitle>
        <DialogContent sx={V4_DIALOG_CONTENT}>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('f-silo-number')}
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                required
                value={createSiloNumber}
                onChange={(e) => setCreateSiloNumber(e.target.value)}
                disabled={isCreating}
                slotProps={{ htmlInput: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('f-silo-name')}
                variant="outlined"
                size="small"
                fullWidth
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                disabled={isCreating}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="inherit"
                sx={V4_OUTLINED_DEFAULT}
                size="small"
                startIcon={<Icon>edit_location_alt</Icon>}
                onClick={() => setCreatePickerOpen(true)}
                disabled={isCreating}
              >
                {t('f-silo-linked-locations')} ({createLocationIds.length})
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" sx={V4_BUTTON_DEFAULT} onClick={() => setCreateOpen(false)} disabled={isCreating}>
            {t('f-com-cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Icon>save</Icon>}
            onClick={handleCreateSave}
            disabled={isCreating}
          >
            {t('f-com-save')}
          </Button>
        </DialogActions>
      </Dialog>

      <LocationPickerDialog
        open={createPickerOpen}
        selectedIds={createLocationIds}
        onClose={() => setCreatePickerOpen(false)}
        onConfirm={({ ids }) => {
          setCreateLocationIds(ids);
          setCreatePickerOpen(false);
        }}
      />
    </Container>
  );
};

export default SilosTab;
